import React, { useState } from "react";

const Sessions = ({ appData, updateAppData }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    start: "",
    end: "",
  });

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const addSession = () => {
    if (
      !formData.title ||
      !formData.start ||
      !formData.end ||
      selectedDays.length === 0
    ) {
      window.alert("Please fill all fields and select at least one day");
      return;
    }

    const newSession = {
      id: Date.now(),
      title: formData.title,
      start: formData.start,
      end: formData.end,
      days: [...selectedDays],
    };

    updateAppData({
      sessions: [...appData.sessions, newSession],
    });

    // Reset form
    setFormData({ title: "", start: "", end: "" });
    setSelectedDays([]);
    setShowAddForm(false);
  };

  const deleteSession = (id) => {
    if (window.confirm("Delete this session?")) {
      updateAppData({
        sessions: appData.sessions.filter((s) => s.id !== id),
      });
    }
  };

  const startSessionNow = (id) => {
    const session = appData.sessions.find((s) => s.id === id);
    if (!session) return;

    const now = new Date();
    const [endHour, endMinute] = session.end.split(":").map(Number);
    const endTime = new Date(now);
    endTime.setHours(endHour, endMinute, 0);

    if (endTime <= now) {
      endTime.setDate(endTime.getDate() + 1);
    }

    const secondsRemaining = Math.floor((endTime - now) / 1000);

    updateAppData({
      activeSession: session,
      timeRemaining: Math.max(secondsRemaining, 25 * 60), // Minimum 25 minutes
      isPaused: false,
    });
  };

  const generateICSContent = () => {
    const now = new Date();
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const dayMapping = {
      Mon: "MO",
      Tue: "TU",
      Wed: "WE",
      Thu: "TH",
      Fri: "FR",
      Sat: "SA",
      Sun: "SU",
    };

    let icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Deep Work Focus Timer//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
    ];

    appData.sessions.forEach((session, index) => {
      const [startHour, startMinute] = session.start.split(":").map(Number);
      const [endHour, endMinute] = session.end.split(":").map(Number);

      // Create event for next occurrence
      const eventStart = new Date();
      eventStart.setHours(startHour, startMinute, 0, 0);

      const eventEnd = new Date();
      eventEnd.setHours(endHour, endMinute, 0, 0);

      // If end time is before start time, it's next day
      if (eventEnd <= eventStart) {
        eventEnd.setDate(eventEnd.getDate() + 1);
      }

      const recurrenceDays = session.days
        .map((day) => dayMapping[day])
        .join(",");

      icsContent.push(
        "BEGIN:VEVENT",
        `UID:focus-session-${session.id}-${index}@deepworkfocustimer.local`,
        `DTSTAMP:${formatDate(now)}`,
        `DTSTART:${formatDate(eventStart)}`,
        `DTEND:${formatDate(eventEnd)}`,
        `SUMMARY:${session.title}`,
        `DESCRIPTION:Deep Work Focus Session - Website blocking will be active during this time`,
        `RRULE:FREQ=WEEKLY;BYDAY=${recurrenceDays}`,
        "CATEGORIES:WORK,FOCUS",
        "STATUS:CONFIRMED",
        "TRANSP:BUSY",
        "END:VEVENT"
      );
    });

    icsContent.push("END:VCALENDAR");
    return icsContent.join("\r\n");
  };

  const exportToCalendar = () => {
    if (appData.sessions.length === 0) {
      window.alert(
        "No sessions to export. Please add some scheduled sessions first."
      );
      return;
    }

    try {
      const icsContent = generateICSContent();
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `deep-work-focus-sessions-${
        new Date().toISOString().split("T")[0]
      }.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      window.alert(
        "Calendar file downloaded! Import it into your calendar app to sync your focus sessions."
      );
    } catch (error) {
      console.error("Error generating calendar file:", error);
      window.alert("Failed to generate calendar file. Please try again.");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <i className="ph ph-calendar-dots text-gray-700 text-xl"></i>
          <h2 className="text-xl font-bold text-gray-900">
            Scheduled Sessions
          </h2>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2"
        >
          <i className={`ph ${showAddForm ? "ph-x" : "ph-plus"} text-sm`}></i>
          {showAddForm ? "Cancel" : "Add Session"}
        </button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <i className="ph ph-text-aa text-gray-500"></i>
                Session Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Morning Deep Work"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ph ph-clock text-gray-500"></i>
                  Start Time
                </label>
                <input
                  type="time"
                  name="start"
                  value={formData.start}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <i className="ph ph-clock text-gray-500"></i>
                  End Time
                </label>
                <input
                  type="time"
                  name="end"
                  value={formData.end}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="ph ph-calendar-blank text-gray-500"></i>
                Repeat Days
              </label>
              <div className="flex gap-2 flex-wrap">
                {weekDays.map((day) => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all duration-200 ${
                      selectedDays.includes(day)
                        ? "bg-blue-500 text-white border-blue-500 shadow-lg"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50"
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={addSession}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2"
              >
                <i className="ph ph-check text-sm"></i>
                Save Session
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2"
              >
                <i className="ph ph-x text-sm"></i>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {appData.sessions.length === 0 ? (
          <div className="text-center py-12">
            <i className="ph ph-calendar-x text-gray-300 text-6xl mb-4"></i>
            <p className="text-gray-500 font-medium">
              No scheduled sessions yet
            </p>
            <p className="text-gray-400 text-sm">
              Add your first session to get started
            </p>
          </div>
        ) : (
          appData.sessions.map((session) => (
            <div
              key={session.id}
              className={`p-5 rounded-xl border-2 transition-all duration-200 ${
                appData.activeSession?.id === session.id
                  ? "bg-emerald-50 border-emerald-200 shadow-lg"
                  : "bg-gray-50 border-gray-200 hover:border-blue-200 hover:shadow-md"
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2 text-lg">
                    {session.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-600 mb-3">
                    <i className="ph ph-clock text-sm"></i>
                    <p className="text-sm font-medium">
                      {session.start} - {session.end}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {session.days.map((day) => (
                      <span
                        key={day}
                        className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium"
                      >
                        {day}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => startSessionNow(session.id)}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center"
                    title="Start Now"
                  >
                    <i className="ph ph-play text-sm"></i>
                  </button>
                  <button
                    onClick={() => deleteSession(session.id)}
                    className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center"
                    title="Delete"
                  >
                    <i className="ph ph-trash text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 p-6 bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl">
        <div className="flex items-center gap-2 mb-2">
          <i className="ph ph-calendar-plus text-amber-600 text-xl"></i>
          <h3 className="font-bold text-amber-800">Calendar Integration</h3>
        </div>
        <p className="text-sm text-amber-700 mb-4 leading-relaxed">
          Export sessions to your calendar app (Google Calendar, Outlook, Apple
          Calendar, etc.)
        </p>
        <button
          onClick={exportToCalendar}
          className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={appData.sessions.length === 0}
        >
          <i className="ph ph-download text-sm"></i>
          Download .ICS File
        </button>
      </div>
    </div>
  );
};

export default Sessions;
